import { useMutation } from '@tanstack/react-query';
import { driverApi } from '@/services/driverApi';

export interface CapturedPhotoRequest {
  dataUrl: string;
  latitude: number;
  longitude: number;
  captureTime: string;
  documentType: string;
  name?: string;
  notes?: string;
}

export interface InspectionReportRequest {
  inspectionType: 'ON_SITE' | 'ON_DROP_OFF';
  vehicleDamaged: boolean;
  latitude?: number;
  longitude?: number;
  notes?: string;
  photos: CapturedPhotoRequest[];
  documentIds?: string[];
}

export interface InspectionReportResponse {
  id: string;
  dispatchId: string;
  inspectionType: string;
  vehicleDamaged: boolean;
  takenBy: string;
  notes?: string;
  documentIds: string[];
  mediaUrls?: string[];
  urgentlyReportId?: string;
  urgentlyStatus?: string;
  submittedAt?: string;
  createdAt: string;
}

/**
 * Submit an inspection report, which the server sends on to Urgently. Each
 * photo's `dataUrl` must be a base64 `data:` URL: the server decodes it to
 * upload the file. The caller says how it went, in its own shell's toast.
 */
export function useSubmitInspectionReport() {
  return useMutation({
    mutationFn: async ({
      dispatchId,
      data,
    }: {
      dispatchId: string;
      data: InspectionReportRequest;
    }) => {
      const response = await driverApi.submitInspectionReport(dispatchId, data);
      // The server answers 201 with success false when Urgently refused the
      // report; returning its data alone read that as sent.
      if (!response.success) {
        throw new Error(
          response.message || 'Urgently did not take the inspection.'
        );
      }
      return response.data;
    },
  });
}
